// prettier-ignore

/* eslint-disable prettier/prettier */
import './polyfills'
import { Poppins_400Regular, Poppins_600SemiBold, Poppins_900Black, useFonts as useGoogleFonts } from '@expo-google-fonts/poppins';
import { BottomSheetModal, BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import {
  NavigationContainer as RNNavigationContainer,
  useNavigationContainerRef,
} from '@react-navigation/native'
import { CommentsPage } from 'components/pages/Comments'
import { BottomSheetModalBackdrop } from 'components/widgets/BottomSheetModalBackdrop'
import { EmptyHandleComponent } from 'components/widgets/EmptyHandleComponent'
import { LiveQueryController } from 'components/partials/LiveQueryController'
import { IS_WEB } from 'constants/platform'
import { RootContext } from 'contexts'
import { Audio } from 'expo-av'
import { useFonts } from 'expo-font'
import * as SplashScreen from 'expo-splash-screen'
import { NavigationContainer } from 'navigators'
import { CoachOnboardingStack } from 'navigators/CoachOnboardingStack'
import { CreatePostStack } from 'navigators/CreatePostStack'
import { DrawerNavigator } from 'navigators/DrawerNavigator'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import { StyleSheet, View } from 'react-native'
import 'react-native-gesture-handler'
import 'react-native-get-random-values'
import * as Sentry from 'sentry-expo'

import { Toast as ToastComponent } from 'components/widgets/Toast'

import { environment, platform } from 'utils/constants'
import { Log } from 'utils/logger'
import { initialize } from 'utils/logrocket'
import { Toast } from 'utils/toast'

import * as serviceWorkerRegistration from './src/serviceWorkerRegistration'
import {TextEditorPage} from "components/pages/TextEditorModal";
import {InfoPage} from "components/pages/InfoModal";
import {DialogModalPage} from "components/pages/DialogModal";
import {LaunchpadPage} from "components/pages/LaunchpadModal";

const styles = StyleSheet.create({
  view: {
    height: '100%',
    overflow: 'hidden',
  },
})

// Initialize logrocket, and start recording sessions
initialize()

Sentry.init({
  dsn: 'https://09e2586dd6734730b83ad781d469e3d0@o1176276.ingest.sentry.io/6273764',
  enableInExpoDevelopment: true,
  debug: !environment.isProd,
})

export const App = () => {
  // Necessary to pass to sibling navigators to perform cross-navigator navigation :)
  const drawerNavigationRef = useNavigationContainerRef()

  const computedStyles = StyleSheet.create({
    bottomSheetModal: {
      padding: 10,
    },
    bottomSheetModalBackground: {
      backgroundColor: '#4578F1',
    },
    bottomSheetModalWhiteBackground: {
      backgroundColor: 'white',
    },
  })

  useFonts({
    // eslint-disable-next-line global-require
    Feather: require('./assets/fonts/Feather.ttf'),
  })

  const [fontsLoaded] = useGoogleFonts({
    Poppins: Poppins_400Regular,
    'Poppins-SemiBold': Poppins_600SemiBold, // Used as our bold text.
    'Poppins-Black': Poppins_900Black,
  })

  useEffect(() => {
    async function prepare() {
      try {
        // Explicitly prevent the splash screen from showing, and then hide it whenever we want; currently, right after
        await SplashScreen.preventAutoHideAsync()
        if (fontsLoaded) {
          await SplashScreen.hideAsync()

          await Audio.setAudioModeAsync({ playsInSilentModeIOS: true })
        }
      } catch (e) {
        Log.warn('Issue with preparing the app', e)
      }
    }

    prepare()
  }, [fontsLoaded])

  const launchpadModalRef = useRef<BottomSheetModal>(null)
  const launchpadModalSnapPoints = useMemo(() => ['80%'], [])

  const coachOnboardingModalRef = useRef<BottomSheetModal>(null)
  const coachOnboardingSnapPoints = useMemo(() => ['90%'], [])

  const createPostModalRef = useRef<BottomSheetModal>(null)
  const createPostSnapPoints = useMemo(() => [250, 520, '90%'], [])

  const commentModalRef = useRef<BottomSheetModal>(null)
  const commentSnapPoints = useMemo(() => [IS_WEB ? '90%' : '50%'], [])

  const textEditorModalRef = useRef<BottomSheetModal>(null)
  const textEditorSnapPoints = useMemo(() => [IS_WEB ? '90%' : '50%'], [])

  const infoModalRef = useRef<BottomSheetModal>(null)
  const infoModalSnapPoints = useMemo(() => ['50%'], [])

  const dialogModalRef = useRef<BottomSheetModal>(null)
  const dialogModalSnapPoints = useMemo(() => [380], [])

  const [primaryBg, setPrimaryBg] = useState<boolean>(false)

  return (
    <RootContext
      launchpadModalRef={launchpadModalRef}
      coachOnboardingModalRef={coachOnboardingModalRef}
      createPostModalRef={createPostModalRef}
      commentModalRef={commentModalRef}
      textEditorModalRef={textEditorModalRef}
      infoModalRef={infoModalRef}
      dialogModalRef={dialogModalRef}
      drawerNavigationRef={drawerNavigationRef}
    >
      <View style={styles.view}>
        <BottomSheetModalProvider>
          <NavigationContainer drawerNavigationRef={drawerNavigationRef} independent>
            <DrawerNavigator coachOnboardingModalRef={coachOnboardingModalRef} />
            <LiveQueryController />
            <ToastComponent />
          </NavigationContainer>
        </BottomSheetModalProvider>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
                index={0}
                ref={launchpadModalRef}
                snapPoints={launchpadModalSnapPoints}
                enableHandlePanningGesture
                enablePanDownToClose
                handleComponent={EmptyHandleComponent}
                backdropComponent={BottomSheetModalBackdrop}
                backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
                style={computedStyles.bottomSheetModal}
            >
              {(props) => <LaunchpadPage {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
                index={0}
                ref={infoModalRef}
                snapPoints={infoModalSnapPoints}
                enableHandlePanningGesture
                enablePanDownToClose
                handleComponent={EmptyHandleComponent}
                backdropComponent={BottomSheetModalBackdrop}
                backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
                style={computedStyles.bottomSheetModal}
            >
              {(props) => <InfoPage {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
                index={0}
                ref={dialogModalRef}
                snapPoints={dialogModalSnapPoints}
                enableHandlePanningGesture
                enablePanDownToClose
                handleComponent={EmptyHandleComponent}
                backdropComponent={BottomSheetModalBackdrop}
                backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
                style={computedStyles.bottomSheetModal}
            >
              {(props) => <DialogModalPage {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
              index={0}
              ref={commentModalRef}
              snapPoints={commentSnapPoints}
              enableHandlePanningGesture
              enablePanDownToClose
              handleComponent={EmptyHandleComponent}
              backdropComponent={BottomSheetModalBackdrop}
              backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
              style={computedStyles.bottomSheetModal}
            >
              {(props) => <CommentsPage drawerNavigationRef={drawerNavigationRef} {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
                index={0}
                ref={textEditorModalRef}
                snapPoints={textEditorSnapPoints}
                enableHandlePanningGesture
                enablePanDownToClose
                handleComponent={EmptyHandleComponent}
                backdropComponent={BottomSheetModalBackdrop}
                backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
                style={computedStyles.bottomSheetModal}
            >
              {(props) => <TextEditorPage drawerNavigationRef={drawerNavigationRef} {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
              ref={coachOnboardingModalRef}
              index={0}
              snapPoints={coachOnboardingSnapPoints}
              enableHandlePanningGesture={false}
              enablePanDownToClose={false}
              handleComponent={EmptyHandleComponent}
              backdropComponent={BottomSheetModalBackdrop}
              backgroundStyle={primaryBg ? computedStyles.bottomSheetModalBackground :  computedStyles.bottomSheetModalWhiteBackground}
              style={computedStyles.bottomSheetModal}
            >
              {(props) => <CoachOnboardingStack setPrimaryBg={setPrimaryBg} drawerNavigationRef={drawerNavigationRef} {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>

        <RNNavigationContainer independent>
          <BottomSheetModalProvider>
            <BottomSheetModal
              ref={createPostModalRef}
              index={0}
              snapPoints={createPostSnapPoints}
              enableHandlePanningGesture={false}
              enablePanDownToClose={false}
              enableContentPanningGesture={false}
              handleComponent={EmptyHandleComponent}
              backdropComponent={BottomSheetModalBackdrop}
              backgroundStyle={computedStyles.bottomSheetModalWhiteBackground}
              style={computedStyles.bottomSheetModal}
            >
              {(props) => <CreatePostStack drawerNavigationRef={drawerNavigationRef} {...props} />}
            </BottomSheetModal>
          </BottomSheetModalProvider>
        </RNNavigationContainer>
      </View>
    </RootContext>
  )
}

if (platform.IS_WEB) {
  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })

        Toast.info(
          'Update Available',
          `A new update is available next time you refresh the app.`,
          false,
          {
            onHide: () => {
              window.location.reload()
            },
            onPress: () => {
              window.location.reload()
            },
            autoHide: false,
          },
        )
      }
    },
  })
}
